import React from "react";
import { useI18n } from "../../i18n";
import { LLC, isLLCFormed } from "../../legal";

export default function Refund() {
  const { lang } = useI18n();
  const C = COPY[lang];
  return (
    <div className="container-cd py-16 max-w-3xl" data-testid="legal-refund-page">
      <div className="overline">{C.kicker}</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[0.92]">{C.title}</h1>
      <div className="mt-4 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        {C.effective}: {LLC.legal_effective_date} · {isLLCFormed ? LLC.name : LLC.pending_label}
      </div>

      <div className="mt-10 space-y-10">
        {C.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-display text-2xl md:text-3xl">{s.h}</h2>
            {(s.p || []).map((para, j) => (
              <p key={j} className="mt-3 text-sm md:text-base" style={{ color: "var(--ink-2)" }}>
                {para}
              </p>
            ))}
            {s.list ? (
              <ul className="mt-3 space-y-2 text-sm md:text-base list-disc pl-6" style={{ color: "var(--ink-2)" }}>
                {s.list.map((li, k) => <li key={k}>{li}</li>)}
              </ul>
            ) : null}
          </section>
        ))}

        <section>
          <h2 className="font-display text-2xl md:text-3xl">{C.contact}</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
            {LLC.name}
            <br />
            {C.refund_requests}: <a className="underline" href={`mailto:${LLC.support_email}`}>{LLC.support_email}</a>
          </p>
        </section>
      </div>
    </div>
  );
}

const COPY = {
  en: {
    kicker: "LEGAL · REFUND & CANCELLATION",
    title: "Refund & Cancellation Policy",
    effective: "Effective",
    contact: "Contact",
    refund_requests: "Refund requests",
    sections: [
      {
        h: "1. Free tier",
        p: [
          "The Listed (Free) tier carries no charge and therefore no refund applies. You may delete a free account at any time from your dashboard or by emailing " + LLC.support_email + ".",
        ],
      },
      {
        h: "2. 60-day money-back guarantee on paid tiers",
        p: [
          "Pro ($199/mo) and Premium ($599/mo) subscriptions include a 60-day money-back guarantee from the date of first payment. If, within the first 60 days, the platform fails to deliver what was promised in the Pricing page (bilingual microsite, vault, share links, RFQ inbox, verification queue), email " + LLC.support_email + " and we will refund the most recent month, no questions asked.",
        ],
      },
      {
        h: "3. Monthly subscription cancellation",
        list: [
          "You may cancel a Pro or Premium subscription at any time from the Stripe customer portal (linked from your dashboard).",
          "Cancellation takes effect at the end of the current billing period. You retain access to paid features until that date.",
          "We do not pro-rate refunds for partial months after the 60-day guarantee window.",
        ],
      },
      {
        h: "4. Founding 10 pricing",
        p: [
          "Founding 10 members receive a lifetime price lock of $149/month (vs $599 standard Premium). Founding pricing requires an uninterrupted, paid subscription. If you cancel and later resubscribe, founding pricing is forfeited and standard pricing applies.",
        ],
      },
      {
        h: "5. Enterprise / IMMEX 4.0 engagements",
        p: [
          "Enterprise engagements ($85K+) are governed by a signed statement of work and are not subject to the 60-day money-back guarantee. Refund and cancellation terms are defined in each statement of work.",
        ],
      },
      {
        h: "6. Disputes and chargebacks",
        p: [
          "Before initiating a chargeback with your card issuer, please contact " + LLC.support_email + ". We aim to resolve refund requests within 5 business days. Chargebacks initiated without prior contact may result in account suspension.",
        ],
      },
      {
        h: "7. Data after cancellation",
        list: [
          "On cancellation, your manufacturer profile is unpublished from the directory within 24 hours.",
          "Uploaded documents and account data are retained for 90 days, during which you may reactivate without data loss.",
          "After 90 days, account data is deleted, except billing records retained 7 years for tax compliance (see Privacy Policy).",
        ],
      },
    ],
  },
  es: {
    kicker: "LEGAL · REEMBOLSOS Y CANCELACIÓN",
    title: "Política de Reembolsos y Cancelación",
    effective: "Vigente desde",
    contact: "Contacto",
    refund_requests: "Solicitudes de reembolso",
    sections: [
      {
        h: "1. Plan gratuito",
        p: [
          "El plan Listed (Gratis) no tiene costo, por lo tanto no aplica reembolso. Puedes eliminar tu cuenta gratuita en cualquier momento desde tu panel o enviando un correo a " + LLC.support_email + ".",
        ],
      },
      {
        h: "2. Garantía de 60 días en planes pagados",
        p: [
          "Las suscripciones Pro ($199/mes) y Premium ($599/mes) incluyen una garantía de devolución de 60 días desde la fecha del primer pago. Si, dentro de los primeros 60 días, la plataforma no entrega lo prometido en la página de Precios (micrositio bilingüe, bóveda, enlaces para compartir, bandeja RFQ, cola de verificación), envía un correo a " + LLC.support_email + " y reembolsaremos el mes más reciente, sin preguntas.",
        ],
      },
      {
        h: "3. Cancelación de suscripción mensual",
        list: [
          "Puedes cancelar una suscripción Pro o Premium en cualquier momento desde el portal de cliente de Stripe (enlazado desde tu panel).",
          "La cancelación surte efecto al final del período de facturación actual. Conservas acceso a las funciones pagadas hasta esa fecha.",
          "No prorrateamos reembolsos por meses parciales después de la ventana de garantía de 60 días.",
        ],
      },
      {
        h: "4. Precios Founding 10",
        p: [
          "Los miembros Founding 10 reciben un precio bloqueado de por vida de $149/mes (vs $599 Premium estándar). El precio Fundador requiere una suscripción pagada ininterrumpida. Si cancelas y vuelves a suscribirte más tarde, pierdes el precio Fundador y aplica el precio estándar.",
        ],
      },
      {
        h: "5. Compromisos Enterprise / IMMEX 4.0",
        p: [
          "Los compromisos Enterprise ($85K+) se rigen por una orden de trabajo firmada y no están sujetos a la garantía de 60 días. Los términos de reembolso y cancelación se definen en cada orden de trabajo.",
        ],
      },
      {
        h: "6. Disputas y contracargos",
        p: [
          "Antes de iniciar un contracargo con tu emisor de tarjeta, contacta a " + LLC.support_email + ". Atendemos las solicitudes de reembolso en 5 días hábiles. Los contracargos iniciados sin contacto previo pueden resultar en suspensión de cuenta.",
        ],
      },
      {
        h: "7. Datos después de la cancelación",
        list: [
          "Al cancelar, tu perfil de fabricante se despublica del directorio en 24 horas.",
          "Los documentos subidos y datos de cuenta se conservan 90 días, durante los cuales puedes reactivar sin pérdida de datos.",
          "Después de 90 días, los datos de cuenta se eliminan, excepto los registros de facturación retenidos 7 años por cumplimiento fiscal (ver Política de Privacidad).",
        ],
      },
    ],
  },
};
